// Global search: jumps directly to an order/job by ID prefix, and offers quick
// links into the People filters for name/email/phone (Firestore has no native
// substring search, so those delegate to the per-page client-side filter).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  documentId,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
} from 'firebase/firestore';
import { Search, PackageSearch, Truck, UserCircle, Users } from 'lucide-react';
import { db } from '../lib/firebase';

interface Hit {
  id: string;
  label: string;
  to: string;
  kind: 'order' | 'job';
}

const ID_RE = /^[A-Za-z0-9_-]{4,}$/;

async function searchIdPrefix(
  coll: 'orders' | 'jobs',
  term: string
): Promise<Hit[]> {
  const snap = await getDocs(
    query(
      collection(db, coll),
      orderBy(documentId()),
      startAt(term),
      endAt(term + '\uf8ff'),
      limit(6)
    )
  );
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    if (coll === 'orders') {
      const pickup = data.pickupLocation as { address?: string } | undefined;
      return {
        id: d.id,
        label: pickup?.address?.split(',')[0]?.trim() || 'Order',
        to: `/orders/${d.id}`,
        kind: 'order' as const,
      };
    }
    const info = data.customerInfo as
      | { name?: string; companyName?: string }
      | undefined;
    return {
      id: d.id,
      label: info?.companyName || info?.name || 'Job',
      to: `/jobs/${d.id}`,
      kind: 'job' as const,
    };
  });
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = term.trim();

  // Debounced ID search across orders + jobs.
  useEffect(() => {
    if (!ID_RE.test(q)) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = window.setTimeout(() => {
      Promise.all([searchIdPrefix('orders', q), searchIdPrefix('jobs', q)])
        .then(([o, j]) => setHits([...o, ...j]))
        .catch(() => setHits([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  // Cmd/Ctrl+K focuses the search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    setTerm('');
    setHits([]);
    navigate(to);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          value={term}
          aria-label="Global search"
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            } else if (e.key === 'Enter') {
              if (hits.length) go(hits[0].to);
              else if (q) go(`/drivers?q=${encodeURIComponent(q)}`);
            }
          }}
          placeholder="Search orders, jobs, people…"
          className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-9 pr-14 text-sm focus:border-brand-500 focus:outline-none"
        />
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:block">
          ⌘K
        </kbd>
      </div>

      {open && q && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {hits.length > 0 && (
            <div className="border-b border-slate-100 py-1">
              {hits.map((h) => (
                <button
                  key={`${h.kind}-${h.id}`}
                  onClick={() => go(h.to)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  {h.kind === 'order' ? (
                    <PackageSearch size={15} className="shrink-0 text-slate-400" />
                  ) : (
                    <Truck size={15} className="shrink-0 text-slate-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{h.label}</span>
                  <span className="shrink-0 font-mono text-[11px] text-slate-400">
                    {h.id.slice(0, 8)}…
                  </span>
                </button>
              ))}
            </div>
          )}

          {loading && hits.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>
          )}

          <div className="py-1">
            <button
              onClick={() => go(`/drivers?q=${encodeURIComponent(q)}`)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <UserCircle size={15} className="shrink-0 text-slate-400" />
              <span>
                Drivers matching “<span className="font-medium">{q}</span>”
              </span>
            </button>
            <button
              onClick={() => go(`/customers?q=${encodeURIComponent(q)}`)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <Users size={15} className="shrink-0 text-slate-400" />
              <span>
                Customers matching “<span className="font-medium">{q}</span>”
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
